import { IMdlCustomDialogConfiguration, MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { GridOptions } from 'ag-grid-community';
import { find, orderBy } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, Subscription, asyncScheduler } from 'rxjs';
import { map, publishReplay, refCount, switchMap, take, observeOn } from 'rxjs/operators';
import { UrlBuilder } from '../../../core';
import { CovanceGridColumn } from '../../../covance-ag-grid';
import { CovanceFilter, FilterFieldValue } from '../../../covance-filter';
import { UserPermissionsStorage } from '../../../user-permissions/user-permissions-storage';
import { AdminEQueryFilterDefinitionsResolver } from '../admin-e-query-filter-definitions-resolver';
import { COORDINATOR_EQUERY_DETAILS_TOKEN, CoordinatorEQueryDetailsDialogComponent } from '../e-query-details/coordinator-e-query-details-dialog.component';
import { CoordinatorEQueryDetailsStorage } from '../e-query-details/coordinator-e-query-details-storage';
import { CoordinatorEQueryDetails } from '../models/e-query-details/coordinator-e-query-details';
import { EQuerySummary } from '../models/e-query-summary';
import { AdminEQuerySummaryColumns } from './admin-e-query-summary-columns';
import { EQuerySummaryStorage } from './e-query-summary-storage';
import { EQuerySummaryFilterItem } from '../models/e-query-summary-filter-item';
import { UserFilter } from '../../../covance-filter/filters-storage';
import { EQueryStatus } from '../../../e-queries/models/e-query-status';

@Component({
       selector: 'admin-e-query-summary',
       template: require('./admin-e-query-summary.component.html'),
       styles: [require('./admin-e-query-summary.component.scss')],
       changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminEQuerySummaryComponent implements OnDestroy {
    public readonly gridOptions: GridOptions = {
        floatingFilter: true,
        enableFilter: false,
        floatingFiltersHeight: 32,
    };
    public readonly columns$: CovanceGridColumn[];
    public readonly eQueriesSummaries$: Observable<EQuerySummary[]>;
    public readonly filterFields$: Observable<FilterFieldValue[]>;
    private readonly filterIsNone$ = new BehaviorSubject<boolean>(true);
    private readonly subscription: Subscription;
    constructor(
        readonly urlBuilder: UrlBuilder,
        private readonly dialogService: MdlDialogService,
        readonly userPermissionsStorage: UserPermissionsStorage,
        private readonly  eQuerySummaryFilterResolver: AdminEQueryFilterDefinitionsResolver,
        private readonly eQuerySummaryStorage: EQuerySummaryStorage,
        private readonly eQuerySummaryInventoryColumns: AdminEQuerySummaryColumns,
        private readonly eQueryDetailsStorage: CoordinatorEQueryDetailsStorage,
        private readonly filterService: CovanceFilter,
    ) {
        this.columns$ = this.eQuerySummaryInventoryColumns.getEQueriesSummariesColumns();
        const filterDefs = this.eQuerySummaryFilterResolver.createAdminFilterItems();
        
        const projectName: keyof EQuerySummaryFilterItem = 'project';
        const observedFields = filterDefs.filter(f => f.def.queryParam === projectName);
        const observableFilterValues$ = this.filterService.getObservableQueryParams(observedFields.map(f => f.def.queryParam)).pipe(
            map(() => observedFields)
        );

        const filterItems$ = this.eQuerySummaryStorage.getEQueryFilterItems(observableFilterValues$).pipe(
            publishReplay(1),
            refCount()
        );

        if (this.filterIsNone$) {
            this.filterService.setValue(find(filterDefs, def => def.def.queryParam === 'age'), { operator: 'Less', day: '7' });
        }
        this.subscription = combineLatest([
            filterItems$.pipe(take(1)),
            this.filterIsNone$
        ]).pipe(
            observeOn(asyncScheduler),
        ).subscribe(([filterItems, filterIsNone]) => {
            if (filterIsNone) {
                const statusName: keyof EQuerySummaryFilterItem = 'status';
                const filterItem = find(filterItems, v => v.status.value === EQueryStatus.Responded);
                if (!!filterItem) {
                    const filterDef = find(filterDefs, def => def.def.queryParam === statusName);
                    if (!!filterDef) {
                        this.filterService.setValue(filterDef, [filterItem.status]);
                    }
                }
            }
        });
        
        this.filterFields$ = filterItems$.pipe(
            map(filterItems => {
                this.filterService.setFiltersItems(filterItems, filterDefs);
                return filterDefs;
            }),
            publishReplay(1),
            refCount());
        const filterFlow$ = this.filterFields$
            .pipe(
                switchMap(fields => this.filterService.listen(fields).pipe(map(() => fields))),
                publishReplay(1),
                refCount()
            );
        this.eQueriesSummaries$ = filterFlow$
            .pipe(
                switchMap((fields) =>
                    this.eQuerySummaryStorage.getEQuerySummaries(fields).pipe(map(this.sortEQueries)),
                ),
                publishReplay(1),
                refCount()
            );
    }

    public async openEQuery(event: any): Promise<void> {
        const equeryDetails: CoordinatorEQueryDetails = await this.eQueryDetailsStorage.getEQueryDetails(event.data.eQueryId).toPromise();
        const configDialog: IMdlCustomDialogConfiguration = {
            component: CoordinatorEQueryDetailsDialogComponent,
            providers: [{
                provide: COORDINATOR_EQUERY_DETAILS_TOKEN, useValue: equeryDetails
            }],
            isModal: true,
            clickOutsideToClose: true,
            classes: 'detail-scrollable'
        };
        this.dialogService.showCustomDialog(configDialog);
    }

    public onFilterChange(userFilter: UserFilter) {
        this.filterIsNone$.next(userFilter == null);
    }

    public ngOnDestroy(): void {
    }

    private sortEQueries = (eQueries: EQuerySummary[]): EQuerySummary[] | null => {
        if (!eQueries) {
            return null;
        }

        return orderBy<EQuerySummary>(eQueries, eq => eq.eQueryAge.ticks, 'desc');
    }
}
